/**
 * Calculates the Grid size based on the length of the array of items to be rendered.
 * For md and sm screen sizes, the grid size is determined by the length of the array.
 * For xs screen size, the grid size is always 12.
 * @param {number} lengthArray - The length of the array of items to be rendered.
 * @returns {object} - An object containing the Grid size for each screen size.
 */
export const getGridSize = (lengthArray: number) => {
    const mdSize = lengthArray === 1 ? 6 : Math.max(2, Math.round(12 / Math.min(lengthArray, 6)));
    const smSize = lengthArray <= 2 ? Math.round(12 / lengthArray) : lengthArray === 3 ? 4 : 6;
    return { xs: 12, md: mdSize, sm: smSize };
};

/**
 * Returns an object containing the dynamic font size for title and description based on the length of the input array.
 * The font size will be adjusted according to the following rules:
 * - If the length is 1, 2, or 3, the font size will be 16px.
 * - If the length is 4, the font size will be 15px.
 * - If the length is 5, the font size will be 14px.
 * - If the length is 6, the font size will be 13px.
 * - If the length is more than 6, the font size will be 12px.
 * @param {number} lengthArray - The length of the array to determine the font size.
 * @returns {object} - An object containing the dynamic font size for title and description.
 */
export const getDynamicFontSize = (lengthArray: number) => {
    switch (true) {
        case lengthArray <= 3:
            return { title: '16px', description: '16px' };
        case lengthArray === 4:
            return { title: '15px', description: '15px' };
        case lengthArray === 5:
            return { title: '14px', description: '14px' };
        case lengthArray === 6:
            return { title: '13px', description: '13px' };
        default:
            return { title: '12px', description: '12px' }; // For more than 6
    }
};

/**
 * Calculates the font size for phrases based on the length of the phrases array by formula
 * @param {number} phrasesLength - The length of the array of phrases to determine the font size.
 * @returns {object} - An object containing the dynamic font size for each screen size.
 */
export const getPhrasesFontSize = (phrasesLength: number) => {
  // Formula: -1.25 * phrasesLength + 23
  const calculatedSize = Math.round(-1.25 * phrasesLength + 23);

  return {
    xs: `${calculatedSize}px`,
    lg: `${Math.round(calculatedSize - 2)}px`,
    xl: `${calculatedSize}px`,
  };
};

/**
 * Calculates the size of the objective image based on the length of the array of objectives.
 * The image size will be adjusted according to the following rules:
 * - The base image size is 80px.
 * - The image size will be reduced by 12px for each objective added to the array.
 * - The minimum image size is 34px.
 * @param {number} objectiveLength - The length of the array of objectives to determine the image size.
 * @returns {object} - An object containing the dynamic image size for height and width.
 */

export const getObjectiveImageSize = (objectiveLength: number) => {
    let size;
    switch (objectiveLength) {
        case 1:
            size = 80; // Larger for single item
            break;
        case 2:
            size = 68; // Medium for 2 items
            break;
        case 3:
            size = 56; // Target size for 3 items
            break;
        case 4:
            size = 48; // Smaller for 4 items
            break;
        case 5:
            size = 42; // Even smaller for 5 items
            break;
        case 6:
            size = 38; // Small for 6 items
            break;
        default:
            size = 34; // Default size for 7+ items
    }

    return {
        height: `${size}px`,
        width: `${size}px`,
    };
};

/**
 * Calculates the image size based on the length of the array of items to be rendered.
 * The image height will be adjusted according to the following rules:
 * - The base image height is 200px.
 * - The image height will be reduced by 20px for each item added to the array.
 * - The minimum image height is 100px.
 * @param {number} lengthArray - The length of the array of items to determine the image size.
 * @returns {object} - An object containing the dynamic image size for height and width.
 */
export const getImageSize = (lengthArray: number) => {
    const baseHeight = 200;
    const minHeight = 100;
    const calculatedHeight = Math.max(minHeight, baseHeight - (lengthArray - 1) * 20);

    return {
        height: `${calculatedHeight}px`,
        width: '100%',
    };
};

/**
 * Calculates the icon scale based on the length of the array of items to be rendered.
 * The icon scale will be adjusted according to the following rules:
 * - The base icon scale is 1.
 * - The icon scale will be reduced to 0.8 for 9 items.
 * - The icon scale will be reduced to 0.5 for 10 items.
 * - The icon scale will be reduced to 0.3 for 11 or more items.
 * @param {number} lengthArray - The length of the array of items to determine the icon scale.
 * @returns {number} - The calculated icon scale.
 */
export const getIconScale = (lengthArray: number) => {
    switch (true) {
        case lengthArray <= 7:
            return 1; // Normal size for 7 or fewer
        case lengthArray <= 9:
            return 0.8; // Slightly smaller for 9
        case lengthArray <= 10:
            return 0.5; // Smaller for 10
        default:
            return 0.3; // Smallest for 11+ phrases
    }
};
